"use client";

/**
 * Calculateur de commission APPORTEUR D'AFFAIRES (B2B partenaires).
 *
 * Le ROI version VTC : le partenaire (conciergerie, hôtel, agence…) saisit ses
 * propres hypothèses (clients recommandés × panier moyen) et le calcul applique
 * la grille de commission RÉELLE du code (COMMISSION_TIERS : 12/17/22 % selon le
 * CA annuel apporté). Aucun chiffre inventé : volumes saisis par l'utilisateur,
 * taux/seuils = source unique partners.ts, sortie estampillée « estimation ».
 *
 * Auto-suffisant : state local, calcul pur (SSR-safe), i18n FR/EN/AR, RTL-safe.
 */
import { useState } from "react";
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import type { Locale } from "@/config/i18n";
import { tr, type I18n } from "@/config/localeText";
import {
  commissionTierFor,
  formatCommissionRate,
  formatEuro,
} from "@/config/partners";

type SimDict = {
  eyebrow: string;
  heading: string;
  intro: string;
  clientsLabel: string;
  basketLabel: string;
  resultEyebrow: string;
  rateCaption: string;
  perYear: string;
  perMonth: string;
  basisLabel: string;
  disclaimer: string;
  cta: string;
};

const SIM_UI: I18n<SimDict> = {
  fr: {
    eyebrow: "Estimez vos revenus",
    heading: "Combien votre établissement gagne en nous recommandant ?",
    intro:
      "Vous touchez une commission sur le chiffre d'affaires que vous nous apportez — 12 %, 17 % ou 22 % selon le volume annuel. Ajustez vos hypothèses :",
    clientsLabel: "Clients recommandés par mois",
    basketLabel: "Panier moyen par client (€)",
    resultEyebrow: "Votre estimation",
    rateCaption: "votre taux de commission",
    perYear: "par an",
    perMonth: "par mois",
    basisLabel: "sur un CA annuel apporté de",
    disclaimer:
      "Estimation indicative basée sur vos hypothèses — ce n'est pas une garantie. Commission versée sur le CA réellement encaissé.",
    cta: "Devenir partenaire",
  },
  en: {
    eyebrow: "Estimate your revenue",
    heading: "How much can your venue earn by referring us?",
    intro:
      "You earn a commission on the revenue you refer to us — 12%, 17% or 22% depending on yearly volume. Adjust your assumptions:",
    clientsLabel: "Referred clients per month",
    basketLabel: "Average basket per client (€)",
    resultEyebrow: "Your estimate",
    rateCaption: "your commission rate",
    perYear: "per year",
    perMonth: "per month",
    basisLabel: "on yearly referred revenue of",
    disclaimer:
      "Indicative estimate based on your assumptions — not a guarantee. Commission paid on revenue actually collected.",
    cta: "Become a partner",
  },
  ar: {
    eyebrow: "احسب إيراداتك",
    heading: "كم يمكن لمنشأتك أن تكسب بترشيحنا؟",
    intro:
      "تحصل على عمولة على الإيرادات التي تُحيلها إلينا — 12٪ أو 17٪ أو 22٪ حسب الحجم السنوي. عدّل افتراضاتك:",
    clientsLabel: "العملاء المُحالون شهريًا",
    basketLabel: "متوسط قيمة العميل (يورو)",
    resultEyebrow: "تقديرك",
    rateCaption: "نسبة عمولتك",
    perYear: "سنويًا",
    perMonth: "شهريًا",
    basisLabel: "على إيرادات سنوية مُحالة قدرها",
    disclaimer:
      "تقدير إرشادي مبني على افتراضاتك — وليس ضمانًا. تُدفع العمولة على الإيرادات المحصّلة فعليًا.",
    cta: "كن شريكًا",
  },
};

/** Parse robuste : virgule décimale tolérée, négatifs/NaN → 0. */
const toNum = (raw: string): number => {
  const v = parseFloat(raw.replace(",", "."));
  return Number.isFinite(v) && v > 0 ? v : 0;
};

type Props = {
  locale?: Locale;
  /** Cible du CTA (ancre du formulaire partenaire embarqué…). */
  ctaHref: string;
};

export function CommissionSimulator({
  locale = "fr",
  ctaHref,
}: Props): JSX.Element {
  const ui = tr(SIM_UI, locale);
  const [clients, setClients] = useState("8");
  const [basket, setBasket] = useState("250");

  const annualRevenue = toNum(clients) * toNum(basket) * 12;
  const tier = commissionTierFor(annualRevenue);
  const commissionYear = (annualRevenue * tier.rate) / 100;
  const commissionMonth = commissionYear / 12;

  const inputCls =
    "mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm font-semibold text-black outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/30";
  const labelCls =
    "block text-xs font-medium uppercase tracking-[0.12em] text-black/55";

  return (
    <section className="bg-white py-14 text-black">
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <p className="mb-2 text-xs uppercase tracking-[0.18em] text-gold-800">
          {ui.eyebrow}
        </p>
        <h2 className="mb-3 font-space-age text-xl tracking-[0.2em]">
          {ui.heading}
        </h2>
        <p className="mb-6 max-w-3xl text-sm text-black/70">{ui.intro}</p>

        <div className="grid gap-5 rounded-2xl border border-black/10 bg-black/[0.02] p-5 md:grid-cols-[0.9fr_1.1fr] md:p-7">
          {/* Hypothèses saisies par le partenaire */}
          <div className="grid content-start gap-4">
            <div>
              <label htmlFor="com-clients" className={labelCls}>
                {ui.clientsLabel}
              </label>
              <input
                id="com-clients"
                type="number"
                inputMode="numeric"
                min={0}
                value={clients}
                onChange={(e) => setClients(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="com-basket" className={labelCls}>
                {ui.basketLabel}
              </label>
              <input
                id="com-basket"
                type="number"
                inputMode="numeric"
                min={0}
                value={basket}
                onChange={(e) => setBasket(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          {/* Résultat : taux réel appliqué + commission estimée */}
          <div className="flex flex-col justify-between rounded-xl border border-primary/30 bg-primary/[0.06] p-5">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-gold-800">
                {ui.resultEyebrow}
              </p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-space-age text-4xl leading-none text-black">
                  {formatCommissionRate(tier.rate, locale)}
                </span>
                <span className="text-sm text-black/70">{ui.rateCaption}</span>
              </div>
              <div className="mt-4 grid gap-2 text-sm text-black/80">
                <p>
                  <span className="font-semibold text-black">
                    ≈ {formatEuro(Math.round(commissionYear), locale)}
                  </span>{" "}
                  {ui.perYear}
                </p>
                <p>
                  <span className="font-semibold text-black">
                    ≈ {formatEuro(Math.round(commissionMonth), locale)}
                  </span>{" "}
                  {ui.perMonth}
                </p>
                <p className="text-black/55">
                  {ui.basisLabel}{" "}
                  {formatEuro(Math.round(annualRevenue), locale)}
                </p>
              </div>
            </div>
            <Link
              href={ctaHref}
              className="mt-5 inline-flex w-fit items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-white"
            >
              <Icon icon="mdi:handshake-outline" className="h-4 w-4" />
              {ui.cta}
            </Link>
          </div>
        </div>

        <p className="mt-3 text-xs text-black/50">{ui.disclaimer}</p>
      </div>
    </section>
  );
}

export default CommissionSimulator;
